// MaintenancePage.tsx

import { useState, useEffect, useRef } from "react";
import BgStar from "../../../assets/icons/bg-star.svg?react";
import "./MaintenancePage.scss";
import { useTranslation } from "react-i18next";

export default function MaintenancePage({
    maintenanceEnd,
}: {
    maintenanceEnd: number;
}) {
    const { t } = useTranslation();
    const [timeLeft, setTimeLeft] = useState({
        hours: 0,
        minutes: 0,
        seconds: 0,
    });

    /* ------------------------- Hidden admin bypass ------------------------- */
    const REQUIRED_COMBOS = 30;
    const comboCountRef = useRef(0);
    const star1DownRef = useRef(false);
    const star2DownRef = useRef(false);
    const comboActiveRef = useRef(false); // true once both are down until both released

    const finishComboIfNeeded = () => {
        if (
            comboActiveRef.current === true &&
            star1DownRef.current === false &&
            star2DownRef.current === false
        ) {
            comboActiveRef.current = false;
            comboCountRef.current += 1;
            // Optional: quiet progress log
            // console.debug(`[maintenance bypass] combo ${comboCountRef.current}/${REQUIRED_COMBOS}`);
            if (comboCountRef.current >= REQUIRED_COMBOS) {
                try {
                    localStorage.setItem("eh__maintenance_bypass", "1");
                } catch {}
                window.location.reload();
            }
        }
    };

    const onPointerDownStar = (which: 1 | 2) => (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (which === 1) star1DownRef.current = true;
        else star2DownRef.current = true;
        if (star1DownRef.current && star2DownRef.current) {
            // Both pressed -> arm a combo
            comboActiveRef.current = true;
        }
    };

    const onPointerUpStar = (which: 1 | 2) => (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (which === 1) star1DownRef.current = false;
        else star2DownRef.current = false;
        finishComboIfNeeded();
    };

    const onPointerCancelStar = (which: 1 | 2) => (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (which === 1) star1DownRef.current = false;
        else star2DownRef.current = false;
        finishComboIfNeeded();
    };

    useEffect(() => {
        const calculateTimeLeft = () => {
            const now = Date.now(); // Current time in milliseconds
            const difference = Math.floor((maintenanceEnd - now) / 1000); // Convert to seconds

            if (difference > 0) {
                const hours = Math.floor(difference / 3600);
                const minutes = Math.floor((difference % 3600) / 60);
                const seconds = difference % 60;

                setTimeLeft({ hours, minutes, seconds });
            } else {
                setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
                // Reload page when countdown ends
                window.location.reload();
            }
        };

        // Calculate immediately
        calculateTimeLeft();

        // Set up interval to update every second
        const timer = setInterval(calculateTimeLeft, 1000);

        // Set up interval to reload page every 3 minutes (180 seconds)
        const reloadTimer = setInterval(
            () => {
                window.location.reload();
            },
            3 * 60 * 1000,
        ); // 3 minutes in milliseconds
        // }, 10 * 1000); // 3 minutes in milliseconds

        // Cleanup intervals on component unmount
        return () => {
            clearInterval(timer);
            clearInterval(reloadTimer);
        };
    }, [maintenanceEnd]);

    return (
        <div className="maintenance-page">
            <h1 className="maintenance-page__title">
                {t("MaintenancePage.title")}
            </h1>

            <div className="maintenance-page__count-down">
                <div className="maintenance-page__count-down__items">
                    <p className="maintenance-page__count-down__items__item hours">
                        {timeLeft.hours.toString().padStart(2, "0")}
                        <span>H</span>
                    </p>
                    <p className="maintenance-page__count-down__items__item minutes">
                        {timeLeft.minutes.toString().padStart(2, "0")}
                        <span>M</span>
                    </p>
                    <p className="maintenance-page__count-down__items__item seconds">
                        {timeLeft.seconds.toString().padStart(2, "0")}
                        <span>S</span>
                    </p>
                </div>
                <p className="maintenance-page__count-down__desc">
                    {t("MaintenancePage.countdown")}
                </p>
            </div>

            <div className="maintenance-page__decorations">
                <BgStar
                    className="bg-star n1"
                    style={{ touchAction: "none", userSelect: "none" }}
                    aria-label="decoration star 1"
                    onContextMenu={(e) => e.preventDefault()}
                    onPointerDown={onPointerDownStar(1)}
                    onPointerUp={onPointerUpStar(1)}
                    onPointerCancel={onPointerCancelStar(1)}
                />
                <BgStar
                    className="bg-star n2"
                    style={{ touchAction: "none", userSelect: "none" }}
                    aria-label="decoration star 2"
                    onContextMenu={(e) => e.preventDefault()}
                    onPointerDown={onPointerDownStar(2)}
                    onPointerUp={onPointerUpStar(2)}
                    onPointerCancel={onPointerCancelStar(2)}
                />
            </div>
        </div>
    );
}
